import type { Container } from 'pixi.js';
import type { CardDef } from '../../../core/card';
import type { CardTitle } from '../../../core/ids';

/**
 * **牌面画法** —— 「这张牌长什么样」。
 *
 * ## 分工
 *
 * ```
 *   CardArt（每种牌一份）          通用层（一份，所有牌共用）
 *   ─────────────────────          ──────────────────────────
 *   往 W×H 的画布里画内容    ──▶    渲进 FBO
 *   不关心比例、不关心位置          决定比例、决定位置、决定怎么歪
 * ```
 *
 * 这就是评审说的「每种牌应当定义自己独立的渲染，长宽比和定位之类应当通用」：
 * **画法只管内容，形状和摆放由通用层统一**。
 *
 * ## 为什么画法不许自己决定尺寸
 *
 * 因为牌面画法的数量会一直涨（56 张 + 将来的扩展包 + shader 版本），
 * 而"牌该多大、什么比例"是一个全项目只有一个答案的问题。
 * 让每个画法自己定，结果是手牌一排起来高矮不齐，改比例要改 56 处。
 *
 * 所以 `build()` 拿到的是**已经定好的 `width` / `height`**，
 * 它要做的只是把内容填满这个矩形。
 */

export interface CardArtContext {
  /** 静态定义：牌名、中文名、描述、特征。逻辑层不许读 `name`，但**画法可以** */
  readonly card: CardDef;

  /** FBO 的尺寸（逻辑像素）。宽高比由 `metrics.ts` 保证 */
  readonly width: number;
  readonly height: number;
}

export interface CardArt {
  /**
   * **内容版本号**。同样版本的牌面会被复用同一张 FBO。
   *
   * 牌面是静态的（画一次就永远一样）就返回常量。
   * 牌面要跟着对局状态变（比如"这张牌现在值几分"）就在这里把状态编进去 ——
   * ⚠️ 但 `card` 是**静态定义**，拿不到对局状态。真要做会变的牌面，
   * 得给这一张牌单独造一个 `CardArt` 实例、把状态闭包进去（见 `docs/card-rendering.md` §5）。
   */
  revision(card: CardDef): string | number;

  /**
   * 把牌面画出来。
   *
   * 返回的容器会被渲染进 `width × height` 的 FBO，**然后立刻销毁** ——
   * 留下来的只有那张纹理。所以不要指望它继续活着接收事件。
   */
  build(ctx: CardArtContext): Container;
}

/**
 * 牌名 → 画法。
 *
 * 查不到就退回 `fallback` —— **永远不能出现"没有画法"的牌**。
 * 加新牌时忘了配画法，结果应该是一张明显是占位符的牌，
 * 而不是一片空白（空白会让人以为是渲染坏了，查半天）。
 */
export class CardArtRegistry {
  readonly #byTitle = new Map<CardTitle, CardArt>();
  readonly #fallback: CardArt;

  constructor(fallback: CardArt) {
    // ⚠️ 不能用构造函数参数属性（`constructor(private readonly x)`）——
    // `erasableSyntaxOnly` 禁止这种不可擦除的语法，见 tsconfig.base.json
    this.#fallback = fallback;
  }

  register(title: CardTitle, art: CardArt): this {
    this.#byTitle.set(title, art);
    return this;
  }

  /** 一次注册一批（同一个画法画好几张牌，例如 5 张无效牌可以共用一个） */
  registerAll(titles: readonly CardTitle[], art: CardArt): this {
    for (const title of titles) this.register(title, art);
    return this;
  }

  get(title: CardTitle): CardArt {
    return this.#byTitle.get(title) ?? this.#fallback;
  }

  has(title: CardTitle): boolean {
    return this.#byTitle.has(title);
  }

  get size(): number {
    return this.#byTitle.size;
  }
}
